// ============================================================================
// PENDING ACTION STORAGE
// Stores and retrieves pending user actions (votes, nominations) in
// localStorage so they can be automatically resumed after sign-in.
// ============================================================================

import {logDataByLevel} from './logging-helpers'

const PENDING_ACTION_KEY = 'pendingBallotAction'
const PENDING_ACTION_MAX_AGE_MS = 90 * 60 * 60 * 1000 // 90 hours (matches cookie expiry)

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingVoteAction {
  type: 'vote'
  entryPhotoImage: string
  entryPhotoName: string
  subCategoryId: string
  groupId: string
  additionalFormFields: {id: string; value: string | boolean | number; field_label: string}[]
  timestamp: number
}

export interface PendingNominationAction {
  type: 'nomination'
  subCategoryId: string
  timestamp: number
}

export type PendingAction = PendingVoteAction | PendingNominationAction

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const storePendingAction = (action: PendingAction): void => {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    localStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(action))
  } catch (error) {
    logDataByLevel({
      level: 'error',
      message: 'Failed to store pending action',
      data: error,
    })
  }
}

// ---------------------------------------------------------------------------
// Retrieve
// ---------------------------------------------------------------------------

export const getPendingAction = (): PendingAction | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = localStorage.getItem(PENDING_ACTION_KEY)
    if (!raw) return null

    const parsed: PendingAction = JSON.parse(raw)

    // Expire stale actions
    if (Date.now() - parsed.timestamp > PENDING_ACTION_MAX_AGE_MS) {
      localStorage.removeItem(PENDING_ACTION_KEY)
      return null
    }

    return parsed
  } catch (error) {
    logDataByLevel({
      level: 'error',
      message: 'Failed to retrieve pending action',
      data: error,
    })
    try {
      localStorage.removeItem(PENDING_ACTION_KEY)
    } catch {
      // Silently ignore cleanup errors
    }
    return null
  }
}

// ---------------------------------------------------------------------------
// Clear
// ---------------------------------------------------------------------------

export const clearPendingAction = (): void => {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    localStorage.removeItem(PENDING_ACTION_KEY)
  } catch {
    // Silently ignore removal errors
  }
}
